<template>
  <b-modal :title="helper.trans('labels.auto_withdrawal_settings')"
           :modelValue="modal !== null"
           footer-class=""
           @update:modelValue="$event === false && (modal = null)">
    <template #default>
      <LoaderWrapper ref="loader">
        <div v-if="requestDTO">
          <div class="row" v-if="account && !account.withdrawalWhitelistsEnabled">
            <div class="col-sm-12">
              <div class="alert alert-danger">{{ helper.trans('labels.autoWithdrawalWarningText')}}</div>
            </div>
          </div>
          <div class="row" v-if="account && account.isAutoTransferEnabled">
            <div class="col-sm-12">
              <div class="alert alert-danger">{{ helper.trans('labels.autoWithdrawalAutoTransferWarningText')}}</div>
            </div>
          </div>
          <div class="row">
            <label for="isAutoWithdrawalEnabled" class="col-sm-6 col-form-label text-capitalize-first">
              <span v-if="requestDTO.isAutoWithdrawalEnabled">{{ helper.trans("labels.isAutoWithdrawalEnabled") }}</span>
              <span v-else>{{ helper.trans("labels.isAutoWithdrawalDisabled") }}</span>
            </label>
            <div class="col-sm-6 d-flex align-items-center">
              <div class="form-check form-switch">
                <input v-model="requestDTO.isAutoWithdrawalEnabled" :disabled="!account?.withdrawalWhitelistsEnabled || account?.isAutoTransferEnabled" class="form-check-input" type="checkbox" id="isAutoWithdrawalEnabled">
              </div>
            </div>
          </div>


          <div class="row mt-2" v-if="requestDTO.isAutoWithdrawalEnabled">
            <div class="col-sm-12 d-flex justify-content-between align-items-center pb-0">
              <label for="autoWithdrawalMinSum" class="col-form-label text-capitalize-first">{{ helper.trans("labels.autoWithdrawalMinSum") }}</label>
            </div>
            <div class="col-sm-12">
              <FormInputWrapper v-slot="parentProps" :errors="helper.getErrors('autoWithdrawal', 'autoWithdrawalMinSum')" >
                <input v-model="requestDTO.autoWithdrawalMinSum"
                       v-maska
                       :disabled="!account?.withdrawalWhitelistsEnabled || account?.isAutoTransferEnabled"
                       data-maska="9 99#"
                       data-maska-tokens="9:[0-9]:repeated"
                       data-maska-reversed
                       :class="parentProps.class"
                       type="text" class="form-control " id="autoWithdrawalMinSum">
              </FormInputWrapper>
            </div>
          </div>

          <div class="row mt-2" v-if="requestDTO.isAutoWithdrawalEnabled">
            <label for="autoWithdrawalAddress" class="col-sm-12 col-form-label text-capitalize-first">
              {{ helper.trans("labels.address.usdt") }}
            </label>
            <div class="col-sm-12">
              <FormInputWrapper v-slot="parentProps" :errors="helper.getErrors('autoWithdrawal','autoWithdrawalAddress')">
                <Multiselect :class="parentProps.class"
                             class="multiselect"
                             v-model="requestDTO.autoWithdrawalAddress"
                             :options="whitelistAddresses"
                             select-label="Select"
                             deselect-label=""
                             :disabled="!account?.withdrawalWhitelistsEnabled || account?.isAutoTransferEnabled"
                             placeholder=""
                             open-direction="bottom"
                             id="autoWithdrawalAddress"
                             :multiple="false"
                             :searchable="false"
                             :clear-on-select="false"
                             :close-on-select="true"
                             :show-no-results="false"
                             :hide-selected="false"
                >

                </Multiselect>
              </FormInputWrapper>
            </div>
          </div>
        </div>
      </LoaderWrapper>
    </template>
    <template #footer>
      <div class="d-flex justify-content-between w-100">
        <button type="button" @click="modal = null" class="btn btn-outline-secondary btn-sm text-capitalize-first">
          {{ helper.trans('button.cancel') }}
        </button>
        <LoaderWrapper ref="saveLoader" :zoom="0.5">
        <button type="button" @click="save()" class="btn btn-outline-primary btn-sm text-capitalize-first">
          {{ helper.trans('button.save') }}
        </button>
        </LoaderWrapper>
      </div>
    </template>
  </b-modal>
</template>

<script lang="ts">

import {computed, defineComponent, ref} from 'vue'
import {reactive, toRefs} from "@vue/reactivity";
import {BModal} from "bootstrap-vue-3";
import LoaderWrapper from "@/components/LoaderWrapper.vue";
import {helper} from "@/service/CommonHelper";
import APIClient from "@/service/APIClient";
import {useNotification} from "@kyvg/vue3-notification";
import CopyButton from "@/components/CopyButton.vue";
import {numberFormatter} from "@/service/NumberFormatter";
import PaginationDTO from "@/models/PaginationDTO";
import {dateHelper} from "@/service/DateHelper";
import ValidationErrorDTO from "@/models/ValidationErrorDTO";
import FormInputWrapper from "@/components/FormInputWrapper.vue";
import Multiselect from "vue-multiselect";
import AutoWithdrawalUpdateRequestDTO from "@/models/request/AutoWithdrawalUpdateRequestDTO";
import AccountDTO from "@/models/AccountDTO";

const api = new APIClient();
const notification = useNotification()

export default defineComponent({
  components: {
    BModal,
    LoaderWrapper,
    CopyButton,
    FormInputWrapper,
    Multiselect
  },
  emits: ['account:updated'],
  setup(props, {emit}) {

    const loader = ref<InstanceType<typeof LoaderWrapper> | null>(null)
    const saveLoader = ref<InstanceType<typeof LoaderWrapper> | null>(null)

    const state = reactive({
      modal: null as boolean|null,
      requestDTO: null as AutoWithdrawalUpdateRequestDTO|null,
      account: null as AccountDTO|null,
      whitelistAddresses: [] as string[],
      errors: {} as ValidationErrorDTO[]|any,
    });

    const getForm = () => {
      return {}
    }

    const show = (account: AccountDTO) => {
      state.modal = true
      state.account = account
      state.requestDTO = {
        isAutoWithdrawalEnabled: account.isAutoWithdrawalEnabled,
        autoWithdrawalAddress: account.autoWithdrawalAddress,
        autoWithdrawalMinSum: account.autoWithdrawalMinSum ? numberFormatter.formatMoney(account.autoWithdrawalMinSum, 0) : null
      } as AutoWithdrawalUpdateRequestDTO
      state.errors = {}

      loadList()
    }

    const save = async () => {
      if (state.account === null) {
        return;
      }

      saveLoader.value?.show()
      try {
        state.account = await api.accountsAutoWithdrawal(state.account.id, {
          ...state.requestDTO!,
          autoWithdrawalMinSum: state.requestDTO?.autoWithdrawalMinSum
            ? Number.parseInt(state.requestDTO!.autoWithdrawalMinSum!.replace(/\s/g, '')).toString()
            : null
        })
        emit('account:updated', state.account)
        state.modal = null
      } catch (e) {
        helper.handleErrors('autoWithdrawal', e)
      } finally {
        saveLoader.value?.hide()
      }
    }

    const loadList = async () => {
      if (state.account === null) {
        return;
      }

      loader.value?.show()
      try {
        const items = (await api.withdrawalWhitelist({limit: 1000, offset: 0} as PaginationDTO, {
          status: 'active',
          accountIds: [state.account?.id]
        })).items
        let addresses = [];
        for (const item of items) {
          addresses.push(item.address)
        }

        state.whitelistAddresses = addresses
      } finally {
        loader.value?.hide()
      }
    }


    return {
      ...toRefs(state),
      show,
      helper,
      getForm,
      numberFormatter,
      dateHelper,
      loader,
      save,
      saveLoader
    }
  }
})
</script>
